import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispute } from '../../context/DisputeContext';
import { getDisputeStatus } from '../../utils/api';

const STATUS_CONFIG = {
  initiated: {
    label: 'Initiated',
    color: 'blue',
    icon: '📝',
    description: 'Your dispute has been created',
  },
  draft: {
    label: 'Assessment Complete',
    color: 'blue',
    icon: '📋',
    description: 'Your assessment is being reviewed',
  },
  pending_attorney_review: {
    label: 'Attorney Review',
    color: 'orange',
    icon: '⚖️',
    description: 'An attorney is reviewing your submission',
  },
  modifications_requested: {
    label: 'Modifications Requested',
    color: 'yellow',
    icon: '✏️',
    description: 'Attorney has requested improvements to your submission',
  },
  invitation_sent: {
    label: 'Invitation Sent',
    color: 'yellow',
    icon: '📧',
    description: 'Waiting for the other party to respond',
  },
  party_b_responding: {
    label: 'Other Party Responding',
    color: 'purple',
    icon: '💬',
    description: 'The other party is providing their perspective',
  },
  analysis: {
    label: 'AI Analysis',
    color: 'indigo',
    icon: '🤖',
    description: 'Our AI is analyzing both perspectives',
  },
  settlement_ready: {
    label: 'Settlement Options Ready',
    color: 'green',
    icon: '✅',
    description: 'Review proposed settlement options',
  },
  attorney_review: {
    label: 'Attorney Review',
    color: 'orange',
    icon: '⚖️',
    description: 'An attorney is reviewing the settlement',
  },
  completed: {
    label: 'Completed',
    color: 'green',
    icon: '🎉',
    description: 'Settlement agreement finalized',
  },
  expired: {
    label: 'Expired',
    color: 'red',
    icon: '⏱️',
    description: 'Response deadline passed',
  },
};

const StatusPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dispute, updateDispute } = useDispute();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // Check if disputeId is in URL query params
    const disputeIdFromUrl = searchParams.get('disputeId');

    if (disputeIdFromUrl) {
      // Load the specific dispute from URL
      fetchDisputeById(disputeIdFromUrl);
    } else if (!dispute || !dispute.id) {
      navigate('/start');
      return;
    } else {
      fetchStatus();
    }

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      if (dispute?.id) {
        fetchStatus();
      } else if (disputeIdFromUrl) {
        fetchDisputeById(disputeIdFromUrl);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [dispute?.id, searchParams]);

  const fetchDisputeById = async (disputeId) => {
    try {
      setRefreshing(true);
      const data = await getDisputeStatus(disputeId);
      updateDispute(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch dispute status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchStatus = async () => {
    if (!dispute?.id) return;

    try {
      setRefreshing(true);
      const data = await getDisputeStatus(dispute.id);
      updateDispute(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (!dispute) {
    return null;
  }

  // Check attorney review status for checkpoint 1
  const effectiveStatus = dispute.attorney_review_status === 'pending'
    ? 'pending_attorney_review'
    : dispute.attorney_review_status === 'modified'
    ? 'modifications_requested'
    : dispute.status;

  const statusConfig = STATUS_CONFIG[effectiveStatus] || STATUS_CONFIG[dispute.status] || STATUS_CONFIG.initiated;
  const progress = calculateProgress(effectiveStatus, dispute.attorney_review_status);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold mb-3">Dispute Status</h1>
        <p className="text-gray-600">
          Track your dispute resolution progress in real-time
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Current Status Card */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="text-5xl mr-4">{statusConfig.icon}</div>
            <div>
              <h2 className="text-2xl font-bold">{statusConfig.label}</h2>
              <p className="text-gray-600">{statusConfig.description}</p>
            </div>
          </div>
          <button
            onClick={fetchStatus}
            disabled={refreshing}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-gray-600 mb-2">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`bg-${statusConfig.color}-500 h-3 rounded-full transition-all duration-500`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h3 className="font-bold mb-4">Resolution Timeline</h3>
          <div className="space-y-4">
            <TimelineItem
              completed={true}
              current={dispute.status === 'initiated'}
              icon="📝"
              title="Dispute Initiated"
              description="You created and classified your dispute"
              timestamp={dispute.created_at}
            />
            <TimelineItem
              completed={['invitation_sent', 'party_b_responding', 'analysis', 'settlement_ready', 'attorney_review', 'completed'].includes(dispute.status)}
              current={dispute.status === 'invitation_sent'}
              icon="📧"
              title="Invitation Sent"
              description="Other party has been invited to respond"
              timestamp={dispute.invitation_sent_at}
            />
            <TimelineItem
              completed={['party_b_responding', 'analysis', 'settlement_ready', 'attorney_review', 'completed'].includes(dispute.status)}
              current={dispute.status === 'party_b_responding'}
              icon="💬"
              title="Other Party Responding"
              description="Awaiting their perspective and parameters"
              timestamp={dispute.party_b_started_at}
            />
            <TimelineItem
              completed={['analysis', 'settlement_ready', 'attorney_review', 'completed'].includes(dispute.status)}
              current={dispute.status === 'analysis'}
              icon="🤖"
              title="AI Analysis"
              description="Analyzing both perspectives for fair settlement"
              timestamp={dispute.analysis_started_at}
            />
            <TimelineItem
              completed={['settlement_ready', 'attorney_review', 'completed'].includes(dispute.status)}
              current={dispute.status === 'settlement_ready'}
              icon="✅"
              title="Settlement Options Ready"
              description="Review and accept settlement proposal"
              timestamp={dispute.settlement_ready_at}
            />
            <TimelineItem
              completed={['attorney_review', 'completed'].includes(dispute.status)}
              current={dispute.status === 'attorney_review'}
              icon="⚖️"
              title="Attorney Review"
              description="Licensed attorney reviewing agreement"
              timestamp={dispute.attorney_review_started_at}
            />
            <TimelineItem
              completed={dispute.status === 'completed'}
              current={dispute.status === 'completed'}
              icon="🎉"
              title="Completed"
              description="Settlement agreement finalized"
              timestamp={dispute.completed_at}
            />
          </div>
        </div>
      </div>

      {/* Dispute Details */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="card">
          <h3 className="font-bold mb-4">Dispute Details</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Dispute ID:</span>
              <span className="font-mono text-xs">{dispute.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-medium capitalize">{dispute.type?.replace('_', ' ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Created:</span>
              <span>{new Date(dispute.created_at).toLocaleDateString()}</span>
            </div>
            {dispute.amount_claimed && (
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Claimed:</span>
                <span className="font-bold">€{parseFloat(dispute.amount_claimed).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="font-bold mb-4">Next Steps</h3>
          <div className="space-y-3 text-sm">
            {dispute.attorney_review_status === 'pending' && (
              <>
                <p className="text-gray-700">Your submission is being reviewed by an attorney.</p>
                <p className="text-gray-600">This is a quality check to ensure your case is ready to proceed.</p>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-600">You'll be notified when the review is complete. This typically takes 1-2 business days.</p>
                </div>
              </>
            )}
            {dispute.attorney_review_status === 'modified' && (
              <>
                <p className="text-gray-700 font-medium text-yellow-800">Attorney feedback is available!</p>
                <p className="text-gray-600">Please review the feedback and revise your submission.</p>
                <button
                  onClick={() => navigate('/feedback')}
                  className="btn-primary w-full mt-3"
                >
                  View Feedback & Revise
                </button>
              </>
            )}
            {dispute.status === 'invitation_sent' && !dispute.attorney_review_status && (
              <>
                <p className="text-gray-700">Waiting for the other party to respond.</p>
                <p className="text-gray-600">They have 48 hours to provide their perspective.</p>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-600">You'll receive an email notification when they respond.</p>
                </div>
              </>
            )}
            {dispute.status === 'party_b_responding' && (
              <>
                <p className="text-gray-700">The other party is currently responding.</p>
                <p className="text-gray-600">We'll analyze both perspectives once they finish.</p>
              </>
            )}
            {dispute.status === 'analysis' && (
              <>
                <p className="text-gray-700">Our AI is analyzing both sides.</p>
                <p className="text-gray-600">This usually takes a few minutes.</p>
              </>
            )}
            {dispute.status === 'settlement_ready' && (
              <>
                <p className="text-gray-700 font-medium">Settlement options are ready for review!</p>
                <button className="btn-primary w-full mt-3">
                  Review Settlement Options
                </button>
              </>
            )}
            {dispute.status === 'attorney_review' && !dispute.attorney_review_status && (
              <>
                <p className="text-gray-700">An attorney is reviewing the settlement.</p>
                <p className="text-gray-600">They'll ensure everything is legally sound.</p>
              </>
            )}
            {dispute.status === 'completed' && (
              <>
                <p className="text-gray-700 font-medium">Your dispute has been resolved!</p>
                <p className="text-gray-600">Check your email for the final settlement agreement.</p>
              </>
            )}
            {dispute.status === 'expired' && (
              <>
                <p className="text-gray-700 font-medium text-red-600">Response deadline expired.</p>
                <p className="text-gray-600">The other party did not respond in time.</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="card bg-gray-50">
        <div className="flex items-start">
          <div className="text-2xl mr-3">💡</div>
          <div>
            <h4 className="font-bold mb-2">Need Help?</h4>
            <p className="text-sm text-gray-700 mb-3">
              If you have questions about your dispute or the process, contact our support team.
            </p>
            <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TimelineItem = ({ completed, current, icon, title, description, timestamp }) => {
  return (
    <div className="flex items-start">
      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg ${
        completed ? 'bg-green-100' : current ? 'bg-blue-100' : 'bg-gray-100'
      }`}>
        {completed ? '✓' : icon}
      </div>
      <div className="ml-4 flex-1">
        <div className="flex items-center justify-between">
          <h4 className={`font-medium ${completed ? 'text-green-700' : current ? 'text-blue-700' : 'text-gray-500'}`}>
            {title}
          </h4>
          {timestamp && (
            <span className="text-xs text-gray-500">
              {new Date(timestamp).toLocaleDateString()}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  );
};

const calculateProgress = (status, attorneyReviewStatus) => {
  // If pending attorney review (checkpoint 1), show progress accordingly
  if (attorneyReviewStatus === 'pending') {
    return 15;
  }
  if (attorneyReviewStatus === 'modified') {
    return 12;
  }

  const progressMap = {
    initiated: 10,
    draft: 15,
    pending_attorney_review: 15,
    modifications_requested: 12,
    invitation_sent: 20,
    party_b_responding: 40,
    analysis: 60,
    settlement_ready: 75,
    attorney_review: 90,
    completed: 100,
    expired: 100,
  };
  return progressMap[status] || 0;
};

export default StatusPage;
